/**
 * Placeholders Component
 *
 * Renders a list or grid of copyable placeholder tags.
 *
 * @package PresszoneComments
 */

import { el, __ } from '../utils/dom.js';

/**
 * Render a list/grid of placeholders.
 *
 * @param {Object} props
 * @param {Array} props.items - Array of objects { tag, desc } or strings.
 * @param {string} props.type - 'grid' (with descriptions) or 'inline' (tags only).
 * @param {Function} props.addNotice - Function to show notifications.
 * @param {string} [props.label] - Optional label for the inline list.
 */
export function Placeholders({ items, type = 'grid', label = null, addNotice }) {
    const isGrid = type === 'grid';

    // Process items to standard format { tag, desc }
    const normalizedItems = items.map(item =>
        typeof item === 'string' ? { tag: item, desc: '' } : item
    );

    if (isGrid) {
        return el('div', { class: 'presszone-comments-placeholders-grid--card' },
            ...normalizedItems.map(item =>
                el('div', { class: 'presszone-comments-placeholder-item' },
                    el('code', {
                        class: 'presszone-comments-placeholder-tag',
                        onclick: () => copyToClipboard(item.tag, addNotice),
                        title: __('Click to copy', 'comments-press-zone')
                    }, item.tag),
                    el('span', { class: 'presszone-comments-placeholder-desc' }, item.desc)
                )
            )
        );
    } else {
        // Inline / List style
        return el('div', { class: 'presszone-comments-placeholders-inline' },
            label ? el('strong', { style: 'margin-inline-end: 8px;' }, label) : null,
            el('div', { class: 'presszone-comments-placeholders-list', style: 'display: inline-flex; flex-wrap: wrap; gap: 8px;' },
                ...normalizedItems.map(item =>
                    el('code', {
                        class: 'presszone-comments-placeholder-tag',
                        onclick: () => copyToClipboard(item.tag, addNotice),
                        title: __('Click to copy', 'comments-press-zone'),
                        style: 'cursor: pointer;'
                    }, item.tag)
                )
            )
        );
    }
}

/**
 * Helper to copy text and show notice.
 */
function copyToClipboard(text, addNotice) {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
        if (addNotice) {
            addNotice(__('Copied', 'comments-press-zone') + ' ' + text, 'success');
        }
    }
}

export default Placeholders;
